import React, { useState, useEffect } from "react";
import { useTermination } from "@/contexts/TerminationContext";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
  FormDescription,
} from "@/components/ui/form";
import { Checkbox } from "@/components/ui/checkbox";
import { Calculator, Trash2 } from "lucide-react";
import { UseFormReturn } from "react-hook-form";
import {
  TerminationReason,
  FinalPayComponent as FinalPayComponentType,
} from "@/types/termination";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import TerminationService from "@/services/terminationService";

// Define pay type options to match backend type_code values
export enum PayType {
  HOLIDAY_PAY = "HOLIDAY_PAY",
  SALARY = "SALARY",
  PILON = "PILON",
  REDUNDANCY_PAY = "REDUNDANCY_PAY",
  OTHER = "OTHER",
}

// Map pay types to display names
const payTypeLabels: Record<PayType, string> = {
  [PayType.HOLIDAY_PAY]: "Holiday Pay",
  [PayType.SALARY]: "Salary",
  [PayType.PILON]: "Pay in lieu of Notice",
  [PayType.REDUNDANCY_PAY]: "Redundancy Pay",
  [PayType.OTHER]: "Other",
};

// Map pay types to descriptions
const payTypeDescriptions: Record<PayType, string> = {
  [PayType.HOLIDAY_PAY]: "Holiday pay owed at termination",
  [PayType.SALARY]: "Regular salary owed at termination",
  [PayType.PILON]: "Payment in lieu of notice",
  [PayType.REDUNDANCY_PAY]: "Statutory redundancy payment",
  [PayType.OTHER]: "Other final payments",
};

// Interface for our extended pay component
interface PayComponent {
  id: string;
  description: string;
  amount: number;
  type: "payment" | "deduction";
  pay_type: PayType; // Add pay_type field
}

// Use ExtendedFinalPayComponent interface from the context
interface ExtendedFinalPayComponent extends FinalPayComponentType {
  name?: string;
  overrideValue?: {
    value: number;
    timestamp: string;
    reason: string;
  };
}

interface FinalPayStepProps {
  form: UseFormReturn<any>;
}

const FinalPayStep: React.FC<FinalPayStepProps> = ({ form }) => {
  const { currentTermination, updateTermination } = useTermination();
  const [payComponents, setPayComponents] = useState<PayComponent[]>([]);
  const [newComponent, setNewComponent] = useState<PayComponent>({
    id: "",
    description: "",
    amount: 0,
    type: "payment",
    pay_type: PayType.OTHER, // Default to "Other"
  });

  // Available components from API
  const [availableComponents, setAvailableComponents] = useState<
    ExtendedFinalPayComponent[]
  >([]);
  const [isLoadingComponents, setIsLoadingComponents] = useState(false);

  // State to track debounced updates
  const [updateTimeout, setUpdateTimeout] = useState<NodeJS.Timeout | null>(
    null,
  );

  // Initialize from context
  useEffect(() => {
    // Skip if we don't have a termination record
    console.log("curr", currentTermination);
    if (!currentTermination) return;

    console.log("Initializing pay components", {
      hasComponents: !!currentTermination.final_pay_components,
      length: currentTermination.final_pay_components?.length,
    });

    // Check if we already have components from the context
    if (
      currentTermination.final_pay_components &&
      currentTermination.final_pay_components.length > 0
    ) {
      console.log("Using existing components from context");
      // Map existing components to include pay_type if missing
      const componentWithPayTypes = currentTermination.final_pay_components.map(
        (component) => ({
          ...component,
          // Use type_code if available, otherwise infer from description or default to OTHER
          pay_type: inferPayTypeFromComponent(component),
        }),
      );
      setPayComponents(componentWithPayTypes);
      return;
    }

    console.log("Creating default components");
    // Initialize with defaults based on UK termination practices
    const initialComponents: PayComponent[] = [];

    // Basic pay through notice period
    if (currentTermination?.noticeCalculation?.payThroughDate) {
      initialComponents.push({
        id: "basic-pay",
        description: payTypeDescriptions[PayType.SALARY],
        amount: 1000, // Default value to make validation pass
        type: "payment",
        pay_type: PayType.SALARY,
      });
    }

    // Holiday pay for unused holiday
    initialComponents.push({
      id: "vacation_payout",  // Use vacation_payout to match earning codes
      description: payTypeDescriptions[PayType.HOLIDAY_PAY],
      amount: 0,
      type: "payment",
      pay_type: PayType.HOLIDAY_PAY,
    });

    // Make sure we always have at least one component
    if (initialComponents.length === 0) {
      initialComponents.push({
        id: "vacation_payout",  // Use vacation_payout to match earning codes
        description: payTypeDescriptions[PayType.HOLIDAY_PAY],
        amount: 0,
        type: "payment",
        pay_type: PayType.HOLIDAY_PAY,
      });
    }

    // Update local state
    setPayComponents(initialComponents);

    // Always update the backend to ensure the components are saved
    if (currentTermination.id) {
      console.log("Saving initial components to backend", initialComponents);
      updateTermination(currentTermination.id, {
        final_pay_components: initialComponents,
      });
    }
  }, [currentTermination?.id]);

  // Fetch available pay components from the API
  useEffect(() => {
    const fetchAvailableComponents = async () => {
      setIsLoadingComponents(true);
      try {
        const components = await TerminationService.getFinalPayComponents();
        setAvailableComponents(components as ExtendedFinalPayComponent[]);

        // Update pay type mappings dynamically based on API response
        if (components.length > 0) {
          const labels: Record<string, string> = {};
          const descriptions: Record<string, string> = {};

          components.forEach((component) => {
            if (component.type_code) {
              // Just use description as the fallback
              labels[component.type_code] = component.description;
              descriptions[component.type_code] = component.description;
            }
          });

          // We could update the mappings here, but for now we'll keep the static ones
          console.log("Available component mappings:", {
            labels,
            descriptions,
          });
        }
      } catch (error) {
        console.error("Error fetching pay components:", error);
      } finally {
        setIsLoadingComponents(false);
      }
    };

    fetchAvailableComponents();
  }, []);

  // Function to infer pay type from component based on name or description
  const inferPayTypeFromComponent = (component: any): PayType => {
    if (component.pay_type) return component.pay_type;

    // If we have a direct type_code, use it if it matches our enum
    if (component.type_code) {
      // Try to match the type_code directly
      if (Object.values(PayType).includes(component.type_code)) {
        return component.type_code as PayType;
      }

      // Try matching on parts of the type_code
      const typeCode = component.type_code.toUpperCase();
      if (typeCode.includes("HOLIDAY")) return PayType.HOLIDAY_PAY;
      if (typeCode.includes("SALARY")) return PayType.SALARY;
      if (typeCode.includes("PILON") || typeCode.includes("LIEU"))
        return PayType.PILON;
      if (typeCode.includes("REDUNDANCY")) return PayType.REDUNDANCY_PAY;
    }

    // Otherwise infer from name or description
    const textToCheck = (
      component.name ||
      component.description ||
      ""
    ).toLowerCase();

    if (textToCheck.includes("holiday")) return PayType.HOLIDAY_PAY;
    if (textToCheck.includes("salary") || textToCheck.includes("wage"))
      return PayType.SALARY;
    if (
      textToCheck.includes("lieu") ||
      textToCheck.includes("notice") ||
      textToCheck.includes("pilon")
    )
      return PayType.PILON;
    if (textToCheck.includes("redundancy")) return PayType.REDUNDANCY_PAY;

    return PayType.OTHER;
  };

  // Shared function to update components with debounce
  const updateComponentsWithDebounce = (updatedComponents: PayComponent[]) => {
    console.log("Updating components with new values", updatedComponents);

    // Clear any existing timeout
    if (updateTimeout) {
      clearTimeout(updateTimeout);
    }

    // Set the local state immediately
    setPayComponents(updatedComponents);

    // Debounce the API call to avoid repeated requests
    const timeout = setTimeout(() => {
      if (currentTermination?.id) {
        console.log("Sending debounced update to backend", updatedComponents);

        // Ensure a direct update rather than merging with existing data
        updateTermination(currentTermination.id, {
          final_pay_components: updatedComponents,
        });
      }
    }, 300); // 300ms debounce - slightly faster

    setUpdateTimeout(timeout);
  };

  const handleAddComponent = () => {
    if (!newComponent.description || newComponent.amount === 0) return;

    // Generate id based on pay_type rather than timestamp
    let componentId = "";

    // Map pay types to consistent ID strings
    switch (newComponent.pay_type) {
      case PayType.HOLIDAY_PAY:
        componentId = "vacation_payout";  // Use vacation_payout to match earning codes
        break;
      case PayType.SALARY:
        componentId = "salary";
        break;
      case PayType.PILON:
        componentId = "additional_pay";  // Map to additional_pay earning code
        break;
      case PayType.REDUNDANCY_PAY:
        componentId = "additional_pay";  // Map to additional_pay earning code
        break;
      case PayType.OTHER:
        componentId = "additional_pay";  // Map to additional_pay earning code
        break;
      default:
        // If it's a custom type_code from API, use that as the id
        componentId = (newComponent.pay_type as string)
          .toLowerCase()
          .replace(/_/g, "-");
    }

    // Add a unique suffix if a component with this id already exists
    const existingIds = payComponents.map((comp) => comp.id);
    if (existingIds.includes(componentId)) {
      let suffix = 1;
      while (existingIds.includes(`${componentId}-${suffix}`)) {
        suffix++;
      }
      componentId = `${componentId}-${suffix}`;
    }

    const componentToAdd = {
      ...newComponent,
      id: componentId,
    };

    const updatedComponents = [...payComponents, componentToAdd];
    updateComponentsWithDebounce(updatedComponents);

    // Reset the form
    setNewComponent({
      id: "",
      description: "",
      amount: 0,
      type: "payment",
      pay_type: PayType.OTHER,
    });
  };

  const handleRemoveComponent = (id: string) => {
    const updatedComponents = payComponents.filter(
      (component) => component.id !== id,
    );
    updateComponentsWithDebounce(updatedComponents);
  };

  const handleComponentChange = (
    id: string,
    field: keyof PayComponent | "type_code",
    value: any,
  ) => {
    const updatedComponents = payComponents.map((component) => {
      if (component.id === id) {
        // If type_code is changing, set pay_type from the type_code
        if (field === "type_code") {
          const selectedComponent = availableComponents.find(
            (c) => c.type_code === value,
          );
          return {
            ...component,
            type_code: value,
            pay_type: value as PayType,
            // Update description based on the selected component if available
            description:
              selectedComponent?.description ||
              component.description,
          };
        }

        // If pay_type is changing, update the description as well
        if (field === "pay_type") {
          return {
            ...component,
            pay_type: value,
            // Update description based on the selected pay type
            description: payTypeDescriptions[value as PayType],
          };
        }

        return {
          ...component,
          [field]: field === "amount" ? parseFloat(value) || 0 : value,
        };
      }
      return component;
    });

    updateComponentsWithDebounce(updatedComponents);
  };

  const calculateTotal = () => {
    return payComponents.reduce((total, component) => {
      return component.type === "payment"
        ? total + component.amount
        : total - component.amount;
    }, 0);
  };

  // Clean up the timeout when the component unmounts
  useEffect(() => {
    return () => {
      if (updateTimeout) {
        clearTimeout(updateTimeout);
      }
    };
  }, [updateTimeout]);

  return (
    <div className="space-y-6">
      <div className="mb-6">
        <p>
          Calculate the employee's final pay, including any outstanding salary,
          holiday pay, and statutory payments. Ensure all deductions such as tax
          and National Insurance are included.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Final Pay Calculation</CardTitle>
          <CardDescription>
            Add all payments and deductions for the employee's final payslip
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Table>
            <TableCaption>Final pay components</TableCaption>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[250px]">Description</TableHead>
                <TableHead className="w-[150px]">Pay Type</TableHead>
                <TableHead>Type</TableHead>
                <TableHead className="text-right">Amount (£)</TableHead>
                <TableHead className="w-[50px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {payComponents.map((component) => (
                <TableRow key={component.id}>
                  <TableCell>
                    <Input
                      value={component.description}
                      onChange={(e) =>
                        handleComponentChange(
                          component.id,
                          "description",
                          e.target.value,
                        )
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <Select
                      value={component.pay_type}
                      onValueChange={(value) =>
                        handleComponentChange(component.id, "type_code", value)
                      }
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select pay type" />
                      </SelectTrigger>
                      <SelectContent>
                        {/* Show available components from API if available, otherwise use static values */}
                        {availableComponents.length > 0
                          ? availableComponents.map(
                              (comp) =>
                                comp.type_code && (
                                  <SelectItem
                                    key={comp.type_code}
                                    value={comp.type_code}
                                  >
                                    {comp.description}
                                  </SelectItem>
                                ),
                            )
                          : Object.entries(payTypeLabels).map(
                              ([value, label]) => (
                                <SelectItem key={value} value={value}>
                                  {label}
                                </SelectItem>
                              ),
                            )}
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id={`payment-${component.id}`}
                        checked={component.type === "payment"}
                        onCheckedChange={(checked) => {
                          handleComponentChange(
                            component.id,
                            "type",
                            checked ? "payment" : "deduction",
                          );
                        }}
                      />
                      <label
                        htmlFor={`payment-${component.id}`}
                        className="text-sm font-medium leading-none"
                      >
                        Payment
                      </label>
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={component.amount}
                      onChange={(e) =>
                        handleComponentChange(
                          component.id,
                          "amount",
                          e.target.value,
                        )
                      }
                      className="text-right"
                    />
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleRemoveComponent(component.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <div className="flex items-end gap-4">
            <FormField
              control={form.control}
              name="newDescription"
              render={({ field }) => (
                <FormItem className="flex-1">
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      value={newComponent.description}
                      onChange={(e) => {
                        field.onChange(e);
                        setNewComponent({
                          ...newComponent,
                          description: e.target.value,
                        });
                      }}
                      placeholder="e.g., Accrued holiday pay"
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="newPayType"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Pay Type</FormLabel>
                  <FormControl>
                    <Select
                      value={newComponent.pay_type}
                      onValueChange={(value) => {
                        field.onChange(value);
                        // Look for selected component from available components
                        const selectedComponent = availableComponents.find(
                          (comp) => comp.type_code === value,
                        );

                        setNewComponent({
                          ...newComponent,
                          pay_type: value as PayType,
                          // Auto-populate description based on selected component or pay type
                          description:
                            selectedComponent?.description ||
                            payTypeDescriptions[value as PayType] ||
                            "",
                        });
                      }}
                    >
                      <SelectTrigger className="w-[180px]">
                        <SelectValue placeholder="Select pay type" />
                      </SelectTrigger>
                      <SelectContent>
                        {/* Show available components from API if available, otherwise use static values */}
                        {availableComponents.length > 0
                          ? availableComponents.map(
                              (comp) =>
                                comp.type_code && (
                                  <SelectItem
                                    key={comp.type_code}
                                    value={comp.type_code}
                                  >
                                    {comp.description}
                                  </SelectItem>
                                ),
                            )
                          : Object.entries(payTypeLabels).map(
                              ([value, label]) => (
                                <SelectItem key={value} value={value}>
                                  {label}
                                </SelectItem>
                              ),
                            )}
                      </SelectContent>
                    </Select>
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="newType"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Type</FormLabel>
                  <FormControl>
                    <div className="flex items-center h-10 space-x-2">
                      <Checkbox
                        id="new-payment"
                        checked={newComponent.type === "payment"}
                        onCheckedChange={(checked) => {
                          field.onChange(checked);
                          setNewComponent({
                            ...newComponent,
                            type: checked ? "payment" : "deduction",
                          });
                        }}
                      />
                      <label
                        htmlFor="new-payment"
                        className="text-sm font-medium leading-none"
                      >
                        Payment
                      </label>
                    </div>
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="newAmount"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Amount (£)</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      type="number"
                      step="0.01"
                      min="0"
                      value={newComponent.amount || ""}
                      onChange={(e) => {
                        field.onChange(e);
                        setNewComponent({
                          ...newComponent,
                          amount: parseFloat(e.target.value) || 0,
                        });
                      }}
                      className="w-28 text-right"
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <Button
              type="button"
              onClick={handleAddComponent}
              className="mb-0.5"
            >
              Add
            </Button>
          </div>
        </CardContent>
        <CardFooter className="flex justify-between bg-gray-50 p-4 rounded-b-md">
          <div className="flex items-center space-x-2">
            <Calculator className="h-5 w-5 text-gray-500" />
            <span className="font-medium">Total Final Payment:</span>
          </div>
          <div className="text-xl font-bold">
            £{calculateTotal().toFixed(2)}
          </div>
        </CardFooter>
      </Card>

      <div className="p-4 bg-blue-50 rounded-md">
        <h3 className="font-medium text-blue-800">UK Employment Law Note</h3>
        <p className="text-sm text-blue-700 mt-2">
          Remember that all statutory payments must be made in the employee's
          final pay. This includes accrued holiday pay and any statutory
          redundancy payment if applicable. Deductions for tax and National
          Insurance should be calculated according to the current tax year's
          rates.
        </p>
      </div>
    </div>
  );
};

export default FinalPayStep;
